import React from "react";
import type { ReactNode } from "react";
import { api, type ConnectorStatusView } from "../api";
import type { SimoChat } from "../views/useSimoChat";
import { approvalInputPreview } from "../views/useSimoChat";
import { groupApprovalsByRun } from "../views/approvalDecisions";
import type { DashboardData } from "./useDashboardData";
import { connectorFamily, ownerConnectorStatusCopy } from "./connectorStatusCopy";

const ORB_LABEL: Record<string, string> = {
  idle: "Bereit", wake: "Hört zu", thinking: "Denkt", speaking: "Spricht",
  acting: "Arbeitet", blocked: "Wartet", confirming: "Wartet auf Go", error: "Fehler",
};

function statusAccent(ok: boolean | null, warn = false): string {
  if (ok === null) return "simo-accent-neutral";
  if (!ok) return "simo-accent-down";
  return warn ? "simo-accent-warn" : "simo-accent-ok";
}

export function Panel({
  title, accent, focused, action, children,
}: {
  title: string;
  accent: string;
  focused?: boolean;
  action?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <section className={`panel simo-panel-in ${accent} ${focused ? "panel-focus" : ""}`}>
      <header className="panel-head">
        <div className="flex items-center gap-2 min-w-0">
          <span className="status-dot" />
          <span className="panel-title truncate">{title}</span>
        </div>
        {action}
      </header>
      <div className="panel-body simo-thin-scroll">{children}</div>
    </section>
  );
}

function Row({ label, value, title }: { label: string; value: ReactNode; title?: string }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3 py-0.5">
      <span className="text-[11px] text-jarvis-muted shrink-0">{label}</span>
      <span className="kpi text-xs text-right truncate min-w-0" title={title}>{value}</span>
    </div>
  );
}

export type ConnectorProposalAction =
  | "setup_request"
  | "reauth_request"
  | "read_scope"
  | "write_policy"
  | "device_scope"
  | "health_check";

const CONNECTOR_PROPOSAL_ACTION_LABELS: Record<ConnectorProposalAction, string> = {
  setup_request: "Connector einrichten",
  reauth_request: "Reauth vorbereiten",
  read_scope: "Leserechte pruefen",
  write_policy: "Write-Policy pruefen",
  device_scope: "Device-Scope klaeren",
  health_check: "Health-Check anfragen",
};

export interface ConnectorControlLearningProposalDraft {
  kind: "policy";
  targetKey: string;
  proposedValue: string;
  source: "connector-control";
  reason: string;
  evidence: {
    schema: "it-simo.connector-control-proposal.v1";
    action: ConnectorProposalAction;
    connectorId: string;
    connectorName: string;
    configured: boolean;
    reachable: boolean;
    gate: "Owner Review";
    runtimeMutation: false;
    noOAuthStart: true;
    noConnectorWrite: true;
    noTokenRead: true;
    noSecretRead: true;
    noNotificationSend: true;
    noExternalApiWrite: true;
    noDeploy: true;
  };
}

export function buildConnectorControlLearningProposal(
  connector: ConnectorStatusView,
  action: ConnectorProposalAction,
  note: string,
): ConnectorControlLearningProposalDraft {
  const label = CONNECTOR_PROPOSAL_ACTION_LABELS[action];
  const family = connectorFamily(connector.id);
  const copy = ownerConnectorStatusCopy(connector);
  const cleanNote = note.trim();
  return {
    kind: "policy",
    targetKey: `connector_control.policy.${connector.id}.${action}`,
    source: "connector-control",
    reason: `Owner requested connector-control review for ${connector.name}.`,
    proposedValue: [
      "Connector Control Proposal",
      `Connector: ${connector.name} (${family})`,
      `Aktion: ${label}`,
      `Status: ${copy.statusLabel}`,
      `Owner-Pfad: ${copy.ownerPath}`,
      `Owner note: ${cleanNote || "Keine Notiz"}`,
      "Gate: Owner Review",
      "Live-Aktion: nein",
    ].join("\n"),
    evidence: {
      schema: "it-simo.connector-control-proposal.v1",
      action,
      connectorId: connector.id,
      connectorName: connector.name,
      configured: connector.configured,
      reachable: connector.reachable,
      gate: "Owner Review",
      runtimeMutation: false,
      noOAuthStart: true,
      noConnectorWrite: true,
      noTokenRead: true,
      noSecretRead: true,
      noNotificationSend: true,
      noExternalApiWrite: true,
      noDeploy: true,
    },
  };
}

function connectorProposalErrorCopy(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  if (/401|missing_session|invalid_session|unauthorized/i.test(message)) {
    return "Owner-Session wurde nicht akzeptiert. Bitte neu anmelden und den Connector-Vorschlag erneut speichern.";
  }
  if (/403|forbidden|insufficient_role/i.test(message)) {
    return "Owner-/Admin-Rechte sind fuer Connector-Vorschlaege erforderlich.";
  }
  return "Connector-Vorschlag konnte nicht gespeichert werden. Es wurde kein Connector verbunden und keine externe Aktion ausgefuehrt.";
}

function ConnectorProposalForm({ connectors, onCreated }: { connectors: ConnectorStatusView[]; onCreated: () => void }): JSX.Element {
  const defaultConnector = connectors.find((connector) => !connector.reachable) ?? connectors[0];
  const [connectorId, setConnectorId] = React.useState(defaultConnector?.id ?? "");
  const [action, setAction] = React.useState<ConnectorProposalAction>("reauth_request");
  const [note, setNote] = React.useState("");
  const [status, setStatus] = React.useState<"idle" | "saving" | "saved" | "error">("idle");
  const [error, setError] = React.useState<string | null>(null);
  const selectedConnector = connectors.find((connector) => connector.id === connectorId) ?? defaultConnector;

  async function submit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!selectedConnector || status === "saving") return;
    setStatus("saving");
    setError(null);
    try {
      const draft = buildConnectorControlLearningProposal(selectedConnector, action, note);
      const result = await api.createLearningProposal(draft);
      if (!result.ok) throw new Error(result.error ?? "connector_proposal_failed");
      setStatus("saved");
      setNote("");
      onCreated();
    } catch (proposalError) {
      setStatus("error");
      setError(connectorProposalErrorCopy(proposalError));
    }
  }

  if (!selectedConnector) {
    return <div className="text-[10px] text-jarvis-muted">Connector Proposal ist verfuegbar, sobald Connectoren gemeldet sind.</div>;
  }

  return (
    <form onSubmit={submit} className="simo-keyboard-safe-editor rounded-md border border-white/10 bg-black/[0.18] px-2.5 py-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[9px] font-semibold uppercase tracking-[0.14em] text-jarvis-muted">Connector Proposal</span>
        <span className="badge bg-jarvis-bg/60 text-[9px] text-jarvis-muted">Review Queue</span>
      </div>
      <label className="mt-2 block text-[10px] text-jarvis-muted">
        Connector
        <select
          className="simo-keyboard-safe-field mt-1 min-h-11 w-full rounded border border-white/10 bg-black/[0.18] px-2 py-2 text-base text-jarvis-text outline-none focus:border-jarvis-accent/60 sm:text-sm"
          aria-label="Connector Proposal Connector"
          value={selectedConnector.id}
          onChange={(event) => setConnectorId(event.target.value)}
        >
          {connectors.map((connector) => (
            <option key={connector.id} value={connector.id}>{connector.name}</option>
          ))}
        </select>
      </label>
      <label className="mt-2 block text-[10px] text-jarvis-muted">
        Aktion
        <select
          className="simo-keyboard-safe-field mt-1 min-h-11 w-full rounded border border-white/10 bg-black/[0.18] px-2 py-2 text-base text-jarvis-text outline-none focus:border-jarvis-accent/60 sm:text-sm"
          aria-label="Connector Proposal Aktion"
          value={action}
          onChange={(event) => setAction(event.target.value as ConnectorProposalAction)}
        >
          {Object.entries(CONNECTOR_PROPOSAL_ACTION_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </label>
      <label className="mt-2 block text-[10px] text-jarvis-muted">
        Notiz
        <textarea
          className="simo-keyboard-safe-field mt-1 min-h-24 w-full resize-y rounded border border-white/10 bg-black/[0.18] px-2 py-2 text-base leading-relaxed text-jarvis-text outline-none focus:border-jarvis-accent/60 sm:text-sm"
          aria-label="Connector Proposal Notiz"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Was soll geprueft oder vorbereitet werden?"
        />
      </label>
      <button
        type="submit"
        className="mt-2 min-h-11 w-full rounded border border-sky-300/40 bg-sky-300/10 px-2 py-1 text-[10px] font-semibold text-sky-100 disabled:cursor-not-allowed disabled:opacity-50"
        disabled={status === "saving"}
      >
        {status === "saving" ? "Speichert..." : "Connector-Vorschlag in Review Queue legen"}
      </button>
      <div className="mt-1 text-[9px] leading-snug text-jarvis-muted">
        Speichert nur ein policy Learning Proposal. Kein OAuth-Start, kein Connector-Write, kein Token-/Secret-Read, kein Notification-Send, kein Deploy.
      </div>
      {status === "saved" && <div className="mt-1 text-[10px] text-emerald-200">Connector-Vorschlag wurde in die Review Queue gelegt.</div>}
      {error && <div className="mt-1 text-[10px] text-amber-200">{error}</div>}
    </form>
  );
}

// ── System Health / Runtime / Modell ──────────────────────────────────
export function HealthPanel({ data }: { data: DashboardData }): JSX.Element {
  const h = data.health;
  const ollamaOk = h?.ollama?.ok ?? null;
  const accent = statusAccent(h ? h.ok && ollamaOk !== false : null, ollamaOk === false);
  return (
    <Panel title="System · Runtime" accent={accent}>
      <Row label="Status" value={h ? (h.ok ? "online" : "degraded") : "—"} />
      <Row label="Version" value={`v${h?.version ?? "—"}`} />
      <Row label="Commit" value={h?.runtime?.commit?.slice(0, 7) ?? "—"} title={h?.runtime?.commit ?? ""} />
      <Row label="Branch" value={h?.runtime?.branch ?? "—"} title={h?.runtime?.branch ?? ""} />
      <Row label="Node" value={h?.runtime?.node ?? "—"} />
      <Row label="LLM" value={ollamaOk == null ? "—" : ollamaOk ? `ollama · ${h?.ollama.models.length ?? 0}` : "offline"} />
    </Panel>
  );
}

// ── AgentGraph Run-Status ──────────────────────────────────────────────
export function AgentPanel({ chat, focused }: { chat: SimoChat; focused: boolean }): JSX.Element {
  const lastAssistant = [...chat.history].reverse().find((m) => m.role === "assistant" && m.meta);
  const meta = lastAssistant?.meta;
  return (
    <Panel title="AgentGraph" accent={`simo-accent-${chat.orb}`} focused={focused}>
      <Row label="Zustand" value={ORB_LABEL[chat.orb] ?? chat.orb} />
      <Row label="Runtime" value="langgraph" />
      <Row label="Modell" value={chat.model || "auto"} title={chat.model || "auto"} />
      {meta && <Row label="Letzter Turn" value={`${meta.toolCalls} Tools · ${meta.iterations} Iter`} />}
      {meta?.latencies && <Row label="Laufzeit" value={`${meta.latencies.totalMs} ms`} />}
      {meta?.sanityStatus && meta.sanityStatus !== "passed" && (
        <Row label="Sanity" value={meta.sanityStatus} />
      )}
    </Panel>
  );
}

// ── Pending Approvals (Legacy-Ansicht, display-only) ───────────────────
export function ApprovalsPanel({
  data, chat, focused,
}: {
  data: DashboardData;
  chat: SimoChat;
  focused: boolean;
}): JSX.Element {
  const pending = data.approvals.filter((a) => a.status === "pending");
  const groups = groupApprovalsByRun(pending);

  return (
    <Panel
      title="Approvals"
      accent={pending.length > 0 ? "simo-accent-confirming" : "simo-accent-neutral"}
      focused={focused}
      action={
        <span className={`kpi text-xs ${pending.length > 0 ? "text-emerald-300" : "text-jarvis-muted"}`}>
          {pending.length}
        </span>
      }
    >
      {pending.length === 0 ? (
        <div className="text-[11px] text-jarvis-muted">Keine offenen Freigaben.</div>
      ) : (
        <ul className="space-y-2 max-h-72 overflow-y-auto simo-thin-scroll pr-1">
          {groups.flatMap((g) => {
            // Gehört der Run zur aktuell geladenen Konversation? Abgleich über
            // runId in der History. Falls nicht, markiert das Badge die fremde Sitzung.
            const inSession = chat.history.some((m) => m.runId === g.runId);
            return g.approvals.map((a) => {
              const preview = approvalInputPreview(a.payloadPreviewJson);
              return (
                <li key={a.id} className="rounded-md border border-amber-500/30 bg-amber-500/5 px-2 py-1.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-xs text-jarvis-text break-words min-w-0">{a.summary}</div>
                    <span className="shrink-0 badge bg-jarvis-bg/60 text-jarvis-muted">{a.riskLevel}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="font-mono text-[10px] text-jarvis-muted truncate min-w-0">{a.toolId}</span>
                    {!inSession && (
                      <span
                        className="shrink-0 badge bg-jarvis-bg/60 text-amber-300/80"
                        title="Run gehört zu einer anderen Konversation."
                      >
                        andere Sitzung
                      </span>
                    )}
                  </div>
                  {preview && (
                    <pre className="mt-1.5 text-[10px] text-jarvis-muted/90 whitespace-pre-wrap break-words bg-jarvis-bg/40 rounded p-1.5 max-h-24 overflow-auto simo-thin-scroll">
                      {preview}
                    </pre>
                  )}
                  <div className="mt-1.5 flex flex-wrap gap-2 items-center">
                    <span className="badge bg-jarvis-bg/60 text-amber-200">Legacy-Aktionspfad blockiert</span>
                    <span className="text-[10px] text-jarvis-muted">Approval Inbox ist read/action-gated.</span>
                  </div>
                </li>
              );
            });
          })}
        </ul>
      )}
    </Panel>
  );
}

// ── Connectoren ────────────────────────────────────────────────────────
export function ConnectorsPanel({ data }: { data: DashboardData }): JSX.Element {
  const cs = data.connectors;
  const reachable = cs.filter((c) => c.reachable).length;
  const configured = cs.filter((c) => c.configured).length;
  const partial = cs.filter((c) => c.configured && !c.reachable).length;
  const off = cs.filter((c) => !c.configured).length;
  const statusClass = (c: typeof cs[number]): string =>
    c.reachable ? "simo-accent-ok" : c.configured ? "simo-accent-warn" : "simo-accent-neutral";
  return (
    <Panel
      title="Connectoren"
      accent={cs.length === 0 ? "simo-accent-neutral" : reachable === cs.length ? "simo-accent-ok" : "simo-accent-warn"}
      action={<span className="kpi text-xs text-jarvis-muted">{reachable}/{cs.length}</span>}
    >
      {cs.length === 0 ? (
        <div className="text-[11px] text-jarvis-muted">Keine Connectoren gemeldet.</div>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-1.5">
            {[
              ["Live", reachable],
              ["Setup", partial],
              ["Off", off],
            ].map(([label, value]) => (
              <div key={label} className="rounded-md border border-jarvis-border/45 bg-jarvis-bg/35 px-2 py-1">
                <div className="text-[9px] uppercase tracking-wider text-jarvis-muted">{label}</div>
                <div className="kpi text-sm leading-none">{value}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] text-jarvis-muted">
            <span>Konfiguriert</span>
            <span className="text-right">{configured}/{cs.length}</span>
            <span>Status-Quelle</span>
            <span className="text-right">read-only</span>
            <span>Writes</span>
            <span className="text-right">Approval + Policy</span>
          </div>
          <ul className="space-y-1.5 max-h-72 overflow-y-auto pr-1 simo-thin-scroll">
            {cs.map((c) => {
              const copy = ownerConnectorStatusCopy(c);
              return (
                <li key={c.id} className="rounded-md border border-jarvis-border/40 bg-jarvis-bg/20 px-2 py-1.5 text-xs">
                  <div className="flex items-center gap-2">
                    <span className={`status-dot ${statusClass(c)}`} />
                    <span className="min-w-0 flex-1 truncate" title={c.name}>{c.name}</span>
                    <span className="badge bg-jarvis-bg/60 text-[9px] text-jarvis-muted">{connectorFamily(c.id)}</span>
                    <span className="text-[10px] text-jarvis-muted shrink-0">{copy.statusLabel}</span>
                  </div>
                  <div className="mt-1 line-clamp-2 text-[10px] leading-snug text-jarvis-muted" title={copy.message}>
                    {copy.message}
                  </div>
                  <div className="mt-1 line-clamp-2 text-[10px] leading-snug text-jarvis-muted/90" title={copy.capability}>
                    Fähigkeit: {copy.capability}
                  </div>
                  <div className="mt-1 line-clamp-2 text-[10px] leading-snug text-jarvis-muted/90" title={copy.evidence}>
                    Evidence: {copy.evidence}
                  </div>
                  <div className="mt-1 line-clamp-2 text-[10px] leading-snug text-jarvis-muted/90" title={copy.ownerPath}>
                    Owner-Pfad: {copy.ownerPath}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1 text-[9px] text-jarvis-muted">
                    <span className="badge bg-jarvis-bg/60">{copy.primaryCta}</span>
                    <span className="badge bg-jarvis-bg/60">{copy.safeCta}</span>
                    <span className="badge bg-jarvis-bg/60">{copy.detailsCta}</span>
                  </div>
                  {copy.risk && (
                    <div className="mt-1 line-clamp-2 text-[10px] leading-snug text-amber-200/80" title={copy.risk}>
                      Risiko: {copy.risk}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          <div className="flex flex-wrap gap-1 text-[9px] text-jarvis-muted">
            <span className="badge bg-jarvis-bg/60">Token-Werte verborgen</span>
            <span className="badge bg-jarvis-bg/60">Connector Writes gated</span>
            <span className="badge bg-jarvis-bg/60">OAuth nur Settings</span>
          </div>
          <ConnectorProposalForm connectors={cs} onCreated={data.refresh} />
        </div>
      )}
    </Panel>
  );
}

// ── Audit / Integrität ─────────────────────────────────────────────────
export function AuditPanel({ data }: { data: DashboardData }): JSX.Element {
  return (
    <Panel title="Audit · Integrität" accent={statusAccent(data.auditOk)}>
      <Row label="Hash-Chain" value={data.auditOk == null ? "—" : data.auditOk ? "verifiziert" : "gebrochen"} />
      <Row label="Approvals gesamt" value={data.approvals.length} />
      <Row label="Quelle" value={data.health?.runtime?.environment ?? "—"} />
    </Panel>
  );
}

